import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import Form from  'deltoid/core/Form';

import { Row, Col } from 'deltoid/core/Blocks/BootstrapContainers';
import { H, Body, Caption } from 'deltoid/core/Blocks/Typography';
import { bankAccountRegex, ifscCodeRegex } from '@utils/validations';
import Dialog from './Dialog';
import { BANK_STATUS } from '@constants/bankConstants';
import FormFooter from '@components/BaseComponents/FormFooter';
import ActionCableConsumer from '@components/BaseComponents/ActionCableConsumer';
import ErrorWrapper from '@components/BaseComponents/ErrorWrapper';
import AuthManager from '@services/AuthManager';

import {
  bankDetailsActions,
  ifscActions,
  ifscSelectors,
  dialogActions,
  merchantSelectors,
  merchantActions,
  uiActions,
  bankDetailsActionTypes,
  masterDataSelectors,
} from '@store/ducks';

const BankDetails = ({
  openSearchIFSC,
  branchDetails,
  fetchBranchDetails,
  submitBankDetails,
  bankDetails,
  closeDialog,
  openDocUpload,
  completed,
  editable,
  why,
  filled: filledProp,
  error,
  merchantDetails,
  openConfirmation,
  updateMerchantDetails,
  loadUI,
  businessEntities,
  getMerchantBusinessDetails,
}) => {
  const [holderNameOptions, setHolderNameOptions] = useState([])
  const [showBankDetails, setShowBankDetails] =  useState(false);
  const [filled, setFilled] = useState(filledProp);
  const {
    register,
    errors,
    handleSubmit,
    setValue,
    watch,
    getValues,
    control,
  } = useForm({
    criteriaMode: 'all',
  });
  const { list } = businessEntities || { list: [] };
  const { name: entityType } =  list.find(
    (entity) => getMerchantBusinessDetails.business_entity_id === entity.id
  ) || { name: null };
  const isSoleProprietorship = entityType === 'Sole Proprietorship';

  useEffect(() => {
    const { pancard_name, business_name, business_pan_name_match } = merchantDetails || {};
    let options = [];
    if (pancard_name) options = options.concat(pancard_name);
    if (business_name && !business_pan_name_match && isSoleProprietorship)
      options = options.concat(business_name);
    setHolderNameOptions(options.map(v => ({ label: v, value: v })));
    setValue(
      'bankHolderName',
      options.length ? { label: options[0], value: options[0] } : null,
      { shouldValidate: true },
    );
  }, [merchantDetails])

  useEffect(() => {
    setShowBankDetails(!!branchDetails);
    if (branchDetails) {
      setValue('ifsc', branchDetails.ifsc_code, { shouldValidate: true });
    };
  }, [branchDetails]);

  useEffect(() => {
    setShowBankDetails(false);
    if (!ifscCodeRegex(getValues('ifsc'))) fetchBranchDetails(getValues('ifsc'));
  }, [watch('ifsc')]);

  const onSubmit = formData => submitBankDetails(
    formData.bankAccountNumber,
    formData.ifsc,
    formData.bankHolderName.value,
  );

  const handleWebsocket = m => {
    console.log('ws-bank', m);
    if (m && m.bank_verification_status) {
      // updateMerchantDetails({ bank_detail: m });
      const { bank_name, branch_name, bank_account_number, holder_name, bank_verification_proof } = m;
      if (m.bank_verification_status === BANK_STATUS.VERIFIED) {
        openConfirmation({
          bankName: bank_name,
          branchName: branch_name,
          bankAccountNumber: bank_account_number,
          holderName: holder_name,
        });
        loadUI({ bank_detail: m });
      }
      if (m.bank_verification_status === BANK_STATUS.PENDING) {
        if (bank_verification_proof){
          closeDialog(bankDetailsActionTypes.resetUploadBankProof);
          loadUI({ bank_detail: m });
        } else {
          loadUI({ bank_detail: m });
          openDocUpload();
        }
      }
    }
  }
  const docUploaded = (
    merchantDetails
    && merchantDetails.bank_detail
    && merchantDetails.bank_detail.bank_verification_proof
  );
  return (
    <>
    {
      filled ? (
        <Row className='p-4'>
          <Col md={5} className='align-self-end pt-2'>
            <H level={6}>{bankDetails ? bankDetails.bank_name : ''}</H>
            <Body>{bankDetails ? bankDetails.branch_name : ''}</Body>
          </Col>
          <Col md={4} className='align-self-end pt-2'>
            <Row noGutters>
              <Caption className='w-50'>AC Number:</Caption>
              <Caption>{bankDetails ? bankDetails.bank_account_number : ''}</Caption>
            </Row>
            <Row noGutters>
              <Caption className='w-50'>Account Name:</Caption>
              <Caption>{bankDetails ? bankDetails.holder_name : 'NA'}</Caption>
            </Row>
          </Col>
          <Col md={3} className='align-self-center text-right'>
            {
              editable && (
                <Form.Button variant='underlined' onClick={() => setFilled(false)} className="p-0 mr-3">
                  Reset
                </Form.Button>
            )}
            {error && (
              <Form.Button type='button' variant='outlined' className='mt-2' onClick={openDocUpload}>
                {`${docUploaded ? 'Re-upload' : 'Upload'} Proof`}
              </Form.Button>
            )}
          </Col>
        </Row>
      ) : (
      <form onSubmit={handleSubmit(onSubmit)}>
      <Row className='p-4'>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="bankHolderName"
            >
              Bank Holder Name
            </Form.Label>
            <Controller
              as={Form.Select}
              control={control}
              id="bankHolderName"
              name="bankHolderName"
              options={holderNameOptions}
              rules={{ required: 'This field can not be empty' }}
              placeholder='Select Bank Holder Name'
              onChange={([v]) => v}
            />
            <ErrorWrapper errors={errors} fieldName='bankHolderName' data-testid='bank-account-holder-error'/>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="bankAccountNumber"
            >
              Bank Account Number
            </Form.Label>
            <Form.Text
              name="bankAccountNumber"
              ref={register({
                required: 'This field cannot be left blank',
                minLength: { value: 3, message: 'Bank detail bank account number is invalid' },
                maxLength: { value: 16, message: 'Bank detail bank account number is invalid' },
                validate: {
                  regex: bankAccountRegex,
                }
              })}
              placeholder='Enter Account Number'
              id="bankAccountNumber"
            />
            <ErrorWrapper errors={errors} fieldName='bankAccountNumber' data-testid='bank-account-number-error'/>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label
              id="ifsc"
              className="d-flex justify-content-between"
            >
              <span className="d-inline-flex align-items-center">IFSC Code</span>
              <Form.Button onClick={openSearchIFSC} variant='underlined' type='button'>
                Search IFSC
              </Form.Button>
            </Form.Label>
            <Form.Text
              name="ifsc"
              ref={register({
                required: 'This field cannot be left blank',
                validate: {
                  regex: ifscCodeRegex,
                  doesNotExist: ifscCode => (
                    ifscCode && branchDetails ? null : 'No data found for this IFSC'
                  )
                }
              })}
              placeholder='Enter IFSC Code'
              id="ifsc"
            />
            {showBankDetails && branchDetails && (
              <Form.Message type='muted'>
                {branchDetails.bank_name}, {branchDetails.branch_name}
              </Form.Message>
            )}
            <ErrorWrapper errors={errors} fieldName='ifsc' data-testid='ifsc-error'/>
          </Form.Group>
        </Col>
      </Row>
      <FormFooter
        tooltipProps={why}
        btnProps={{
          label: 'Connect Bank Account',
          type: "submit",
        }}
      />
      </form>
    )}
    <Dialog onClose={closeDialog} />
    {AuthManager.getMerchantUUID() && <ActionCableConsumer
        channelName='ProfileChannel'
        identifier={AuthManager.getMerchantUUID()}
        action={handleWebsocket}
      />}
    </>
  )
};

const mapStateToProps = state => ({
  branchDetails: ifscSelectors.getIFSCDetails(state),
  bankDetails: merchantSelectors.getBankDetails(state),
  merchantDetails: merchantSelectors.getMerchantDetails(state),
  businessEntities: masterDataSelectors.businessEntities(state),
  getMerchantBusinessDetails:
    merchantSelectors.getMerchantBusinessDetails(state),
});

const mapDispatchToProps = dispatch => ({
  openSearchIFSC: () => dispatch(dialogActions.open('search-ifsc')),
  fetchBranchDetails: ifsc_code => dispatch(ifscActions.fetchBranchDetails(ifsc_code)),
  submitBankDetails: (accountNumber, ifscCode, holdeName) => 
    dispatch(bankDetailsActions.submitBankDetails(accountNumber, ifscCode, holdeName)),
  closeDialog: (actionType) => dispatch(dialogActions.close(actionType)),
  openDocUpload: () => dispatch(dialogActions.open('upload-bank-proof')),
  openConfirmation: (props) => dispatch(dialogActions.open('bank-confirmation', props)),
  updateMerchantDetails: (details) => dispatch(merchantActions.updateMerchantDetails(details)),
  loadUI: (details) => dispatch(uiActions.loadUI(details)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BankDetails);
